import React, { useEffect, useState } from 'react';
import { Button } from '../../components';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { toast } from 'react-toastify';
import { invoiceService } from '../../services/InvoiceService';
import { PaymentType } from '../../common/Data';
import { FormGroup, Input, Label } from 'reactstrap';
import { APIUriAms } from '../../services/HttpHelper';
import * as validator from 'validator';

const currencyFormatter = new Intl.NumberFormat(`en-US`, {
    style: `currency`,
    currency: `USD`,
});

const PaymentForm = ({ invoiceId, amount, professionIds, paymentIntentId, recipientEmail, onEmailChange }) => {
    const stripe = useStripe();
    const elements = useElements();

    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [emailAddress, setEmailAddress] = useState<string>(recipientEmail);
    const [isEmailValid, setIsEmailValid] = useState<boolean>();

    const formatMoneyOrZero = (amt: number | undefined | null): string => {
        return currencyFormatter.format(amt || 0);
    };

    useEffect(() => {
        if (!!emailAddress && isEmailValid) {
            onEmailChange(emailAddress);
        }
    }, [emailAddress, isEmailValid, onEmailChange]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!!!stripe || !!!elements) {
            return;
        }
        const toastId = toast.info('Processing payment. Do not refresh or navigate away from this page.', {
            closeOnClick: false,
            draggable: false,
            autoClose: false,
        });
        setIsProcessing(true);
        const result = await stripe.confirmPayment({
            elements,
            redirect: 'if_required',
            confirmParams: {
                /* eslint-disable @typescript-eslint/naming-convention */
                receipt_email: emailAddress,
            },
        });
        if (result.error) {
            toast.update(toastId, {
                type: 'error',
                render: `There was an error processing your payment: ${result.error.message}`,
                autoClose: 5000,
                closeOnClick: true,
                draggable: true,
            });
        } else if (result?.paymentIntent?.status === 'requires_action') {
            toast.update(toastId, {
                type: 'warning',
                render: `This payment type requires additional processing steps. For an immediate payment please use another payment method.`,
                autoClose: 5000,
                closeOnClick: true,
                draggable: true,
            });
            window.location.href = `${window.location.origin}/payments/thank-you?receipt_url=""&success=false`;
        } else if (result?.paymentIntent?.status === 'succeeded' || result?.paymentIntent?.status === 'processing') {
            try {
                const paymentResult = await invoiceService.saveInvoicePayment({
                    invoiceId,
                    isDeleted: false,
                    isRefund: false,
                    isConfirmed: result?.paymentIntent?.status === 'succeeded',
                    paymentAmount: parseFloat(amount.toFixed(2)),
                    convenienceFee: 0,
                    paymentType:
                        result?.paymentIntent?.status === 'processing'
                            ? PaymentType['ACH']
                            : PaymentType['Credit Card'],
                    professionIds,
                    transactionId: paymentIntentId,
                    address: undefined,
                    checkInformation: undefined,
                    city: undefined,
                    country: undefined,
                    createDate: undefined,
                    email: undefined,
                    firstName: undefined,
                    id: 0,
                    lastName: undefined,
                    paidBy: undefined,
                    paymentDate: new Date(),
                    referenceNumber: undefined,
                    rowVersion: undefined,
                    state: undefined,
                    zipCode: undefined,
                    receiptUrl: undefined,
                });
                if (result?.paymentIntent?.status === 'succeeded') {
                    window.location.href = `${window.location.origin}/payments/thank-you?receipt_url="${paymentResult.receiptUrl}"&success=true`;
                    toast.update(toastId, {
                        type: 'success',
                        render: 'Payment processed successfully.',
                        autoClose: 3000,
                        closeOnClick: true,
                        draggable: true,
                    });
                } else {
                    window.location.href = `${window.location.origin}/payments/thank-you?receipt_url=""&success=false`;
                    toast.update(toastId, {
                        type: 'warning',
                        render: `This payment is processing and will be applied when the ACH transaction is completed.`,
                        autoClose: 5000,
                        closeOnClick: true,
                        draggable: true,
                    });
                }
            } catch (ex) {
                toast.update(toastId, {
                    type: 'warning',
                    render: `There was an error recording your payment, please contact CAAHEP to ensure your payment is properly recorded providing the following Transaction Number: ${paymentIntentId}`,
                    autoClose: 5000,
                    closeOnClick: true,
                    draggable: true,
                });
            }
        } else {
            toast.update(toastId, {
                type: 'error',
                render: `There was an error processing your payment. Please try again.`,
                autoClose: 5000,
                closeOnClick: true,
                draggable: true,
            });
        }
        setIsProcessing(false);
    };

    return (
        <>
            <h4 className={`mb-3`}>Payment Form</h4>
            <div className={`mb-3`}>
                {amount > 0 && <h5>Payment Amount: {formatMoneyOrZero(amount)}</h5>}
                {amount === 0 && <h5>Please select at least one line item above to pay</h5>}
            </div>
            <form onSubmit={handleSubmit}>
                <PaymentElement />
                <FormGroup className={`mt-1`}>
                    <Label>{`Email Address`}</Label>
                    <Input
                        type={'email'}
                        value={emailAddress}
                        onChange={(e) => {
                            setEmailAddress(e.target.value);
                            setIsEmailValid(validator.isEmail(e.target.value));
                        }}
                    />
                </FormGroup>
                <div className={`mt-3`}>
                    {`By entering your credit card information and clicking the Submit Payment
                     button, you are authorizing CAAHEP to process your credit card for a 
                     one-time payment in the amount of ${formatMoneyOrZero(amount)}.`}
                </div>
                <div className={`mt-3`}>
                    <a href={`${APIUriAms}/document/download-w9`} target="_blank" rel="noopener noreferrer">
                        Download CAAHEP W-9
                    </a>
                </div>
                <Button
                    type={`submit`}
                    className={`mt-3`}
                    disabled={amount === 0 || !!!stripe || isProcessing || !!!emailAddress}
                >
                    Submit Payment
                </Button>
            </form>
        </>
    );
};

export default PaymentForm;
